import React, { useEffect } from 'react';
import { ArrowLeft, Shield, Github, Smartphone, Target, Zap, Trophy, Volume2, Vibrate, Gamepad2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BubbleShooter = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const features = [
    {
      icon: Target,
      title: 'Aim Prediction',
      description: 'Advanced aim line with wall bounce visualization helps you plan perfect shots.',
      color: '#FF6B6B'
    },
    {
      icon: Zap,
      title: 'Combo System',
      description: 'Chain multiple pops together for score multipliers and bonus points.',
      color: '#FFE66D'
    },
    {
      icon: Trophy,
      title: 'Endless Mode',
      description: 'Progressive difficulty with new colors unlocked as you advance.',
      color: '#4ECDC4'
    },
    {
      icon: Volume2,
      title: 'Sound Effects',
      description: 'Satisfying pop sounds and background music enhance the gameplay experience.',
      color: '#A855F7'
    },
    {
      icon: Vibrate,
      title: 'Haptic Feedback',
      description: 'Feel every pop and bounce with immersive haptic feedback on supported devices.',
      color: '#3B82F6'
    },
    {
      icon: Gamepad2,
      title: 'Universal App',
      description: 'Optimized for all iPhones and iPads with responsive design.',
      color: '#22C55E'
    }
  ];

  const bubbleColors = ['#FF6B6B', '#3B82F6', '#22C55E', '#FFE66D', '#A855F7', '#FF9F43'];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a1035] via-[#2d1b4e] to-[#1a1035] relative overflow-hidden">
      {/* Decorative bubbles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-16 h-16 rounded-full bg-[#FF6B6B]/20 blur-xl"></div>
        <div className="absolute top-40 right-20 w-24 h-24 rounded-full bg-[#3B82F6]/20 blur-xl"></div>
        <div className="absolute top-60 left-1/4 w-12 h-12 rounded-full bg-[#22C55E]/20 blur-xl"></div>
        <div className="absolute bottom-40 right-1/4 w-20 h-20 rounded-full bg-[#FFE66D]/20 blur-xl"></div>
        <div className="absolute bottom-20 left-20 w-14 h-14 rounded-full bg-[#A855F7]/20 blur-xl"></div>
      </div>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#1a1035]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Portfolio</span>
          </button>
          <div className="flex items-center gap-3">
            <img
              src="/images/bubbleshooter-logo.png"
              alt="Bubble Shooter"
              className="w-10 h-10 rounded-xl"
            />
            <span className="font-semibold text-white">Bubble Shooter</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 relative">
        <div className="max-w-4xl mx-auto flex flex-col items-center text-center">
          <div className="relative mb-8">
            <img
              src="/images/bubbleshooter-logo.png"
              alt="Bubble Shooter"
              className="w-40 h-40 rounded-[2rem] shadow-2xl shadow-purple-500/30 mx-auto"
            />
            <div className="absolute -inset-4 rounded-[2.5rem] bg-gradient-to-r from-[#FF6B6B] via-[#A855F7] to-[#3B82F6] opacity-30 blur-xl -z-10"></div>
          </div>

          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-white/20">
            <Smartphone className="w-4 h-4 text-[#A855F7]" />
            <span className="text-white/90 text-sm font-medium">iOS Game</span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            Bubble Shooter
          </h1>
          <p className="text-white/70 text-xl max-w-2xl mx-auto mb-8">
            A polished, addictive Bubble Shooter game for iPhone and iPad. Classic gameplay meets modern design with satisfying haptics and smooth 60 FPS performance.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://github.com/deeplearnproger/BubbleShooter"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#A855F7] to-[#3B82F6] hover:opacity-90 transition-opacity text-white px-6 py-3 rounded-full font-medium shadow-lg shadow-purple-500/30"
            >
              <Github className="w-5 h-5" />
              View on GitHub
            </a>
            <button
              onClick={() => navigate('/bubbleshooter-privacy')}
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-colors text-white px-6 py-3 rounded-full font-medium border border-white/20"
            >
              <Shield className="w-5 h-5" />
              Privacy Policy
            </button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-10">Features</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-colors"
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${feature.color}20` }}
                >
                  <feature.icon className="w-6 h-6" style={{ color: feature.color }} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-white/60 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Game Mechanics */}
      <section className="pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <h2 className="text-2xl font-bold text-white text-center mb-8">Game Mechanics</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-4xl font-bold bg-gradient-to-r from-[#FF6B6B] to-[#FF9F43] bg-clip-text text-transparent mb-2">10 pts</div>
                <p className="text-white/60 text-sm">Per bubble popped</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold bg-gradient-to-r from-[#22C55E] to-[#4ECDC4] bg-clip-text text-transparent mb-2">3x</div>
                <p className="text-white/60 text-sm">Floating bubble bonus</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold bg-gradient-to-r from-[#A855F7] to-[#3B82F6] bg-clip-text text-transparent mb-2">+0.5x</div>
                <p className="text-white/60 text-sm">Combo multiplier</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <h2 className="text-2xl font-bold text-white text-center mb-6">Built With</h2>
            <div className="flex flex-wrap justify-center gap-3">
              {['Swift', 'SpriteKit', 'GameplayKit', 'AVFoundation', 'CoreHaptics', 'UIKit'].map((tech, index) => (
                <span
                  key={index}
                  className="px-4 py-2 rounded-full text-sm font-medium border"
                  style={{
                    backgroundColor: `${bubbleColors[index]}15`,
                    borderColor: `${bubbleColors[index]}40`,
                    color: bubbleColors[index]
                  }}
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <h2 className="text-2xl font-bold text-white text-center mb-6">Requirements</h2>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-3xl font-bold text-[#FF6B6B] mb-2">iOS 15+</div>
                <p className="text-white/60 text-sm">Minimum iOS Version</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#3B82F6] mb-2">Xcode 15+</div>
                <p className="text-white/60 text-sm">Development Environment</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#22C55E] mb-2">Swift 5.0+</div>
                <p className="text-white/60 text-sm">Programming Language</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-6 text-center">
        <p className="text-white/40 text-sm">
          &copy; 2024 Bubble Shooter. All rights reserved.
        </p>
        <div className="flex items-center justify-center gap-4 mt-3">
          <a
            href="https://github.com/deeplearnproger/BubbleShooter"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/40 hover:text-white transition-colors text-sm"
          >
            GitHub
          </a>
          <span className="text-white/20">·</span>
          <button
            onClick={() => navigate('/bubbleshooter-privacy')}
            className="text-white/40 hover:text-white transition-colors text-sm"
          >
            Privacy Policy
          </button>
          <span className="text-white/20">·</span>
          <button
            onClick={() => navigate('/')}
            className="text-white/40 hover:text-white transition-colors text-sm"
          >
            Portfolio
          </button>
        </div>
      </footer>
    </div>
  );
};

export default BubbleShooter;
